import {animate, state, style, transition, trigger} from '@angular/animations';
import {Component, OnInit} from '@angular/core';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import {ActivatedRoute, Router} from '@angular/router';
import {Attachment, Submission} from 'src/app/models/submission';
import {CopyleaksServiceHelper} from 'src/app/services/copyleaks.service';
import { Location, NgIf, NgClass, NgFor, DatePipe } from '@angular/common';
import {InitiateRequest} from 'src/app/models/initiateRequest';
import {SimilarityService} from 'src/app/services/similarity.service';
import {Status} from 'src/app/models/status';
import {UserTypes} from 'src/app/models/userTypes';
import {USER_TYPE} from "../../models/constants";
import { HeaderComponent } from '../header/header.component';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    imports: [HeaderComponent, MatFormField, MatLabel, MatInput, FormsModule, MatSelect, MatOption, MatButton, NgIf, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatIcon, NgClass, NgFor, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, DatePipe]
})
export class DashboardComponent implements OnInit {

  public StatusEnum = Status;

  similarityDashboardData = "SIMILARITY_DASHBOARD_DATA";

  studentId: string = "";
  filterBy: string = "";
  filterValue: string = "";

  isEvaluator: boolean = false;
  isAdmin: boolean = false;

  displayedColumns = ['dateSubmitted', 'taskName', 'attempt', 'assessmentCode', 'files', 'submissionId', 'taskId', 'expanded'];
  
  expandedElement!: Submission | null;

  submissionList: Submission[] = [];
  filteredSubmissionList: Submission[] = [];
  pagedSubmissionList: Submission[] = [];

  taskList: any[] = [];

  currentStudentId = "";

  //pagination
  length = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25];
  hidePageSize = false;
  showPageSizeOptions = true;
  showFirstLastButtons = true;
  disabled = false;
  pageEvent!: PageEvent;
  role:UserTypes = UserTypes.EMA;

  constructor(
    private copyLeaksService: CopyleaksServiceHelper,
    private similarityService: SimilarityService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location
  ) { }

  ngOnInit(): void {
    this.role = sessionStorage.getItem(USER_TYPE) as UserTypes;
    console.log("user role: ", this.role);
    this.initiateStorage();
    const routeStudentId = this.route.snapshot.paramMap.get('studentId');
    if (routeStudentId) this.studentId = routeStudentId;
    if(this.studentId.length > 0){
      this.getStudentSubmissions(this.studentId);
    }

    window.scroll({ 
      top: 0, 
      left: 0, 
      behavior: 'smooth' 
    });
  }

  getStudentSubmissions(studentId: string) {
    this.currentStudentId = this.studentId;
    this.copyLeaksService.getStudentSubmissions(studentId,this.role).subscribe(res => {
      this.submissionList = res;

      if(this.filterBy && this.filterValue) {

        switch (this.filterBy) {
          case "assessmentCode":
            this.filteredSubmissionList = this.submissionList.filter(submission => 
              submission.assessmentCode.toLowerCase().includes(this.filterValue.toLowerCase())
            );
            break;
          case "submissionId":
            this.filteredSubmissionList = this.submissionList.filter(submission => 
              submission.submissionId.toString().toLowerCase().includes(this.filterValue.toLowerCase())
            );
            break;
          case "taskId":
            this.filteredSubmissionList = this.submissionList.filter(submission => 
              submission.taskId.toString().toLowerCase().includes(this.filterValue.toLowerCase())
            );
            break;
        
          default:
            this.filteredSubmissionList = this.submissionList;
            break;
        }
      } else {
        this.filteredSubmissionList = this.submissionList;
      }

      //default sort
      this.filteredSubmissionList.sort((a,b) => this.compare(a.dateSubmitted, b.dateSubmitted, false));

      this.pageIndex = 0;
      
      this.getPagedResults(0);
    })
  }

  navigateToScan(studentId: string, attachmentId: number) {
    const scanId = studentId.toLowerCase() + "_" + attachmentId.toString().toLowerCase();

    if (this.role == UserTypes.EMA) {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      this.router.navigateByUrl(`/scan/${scanId}`);
    } else {
      const parts = scanId.split('-');
      const samScanId = `${parts[2]}-${parts[3]}-${parts[4]}`;
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      this.router.navigateByUrl(`sam/scan/${samScanId}`);
    }
  }

  expandedClick(row: any) {
    if(row.attachments){
      this.expandedElement = this.expandedElement === row ? null : row;
      if ( !this.taskList.find(x => x.taskId === row.taskId) ) {
        this.copyLeaksService.getTask(row.taskId,this.role).subscribe(
          res => {
            this.taskList.push(res.task);
            this.checkAttachments(res.task.excludeFileTypes, row);
          }
        )
      }
    }
  }

  checkAttachments(excludeTypes: string, row: Submission) {
    row.attachments.forEach((attachment: Attachment) => {
      //Check file extension
      let fileExt = attachment.title.split('.').pop();
      if( fileExt && excludeTypes.includes(fileExt) ) {
        attachment.excludedType = true;
      }

      //Check for scanning
      this.checkForScan(attachment);
    });
  }

  initiateScan(submission: Submission, file: Attachment) {
    file.status = this.StatusEnum.processing;
    const initiateRequest: InitiateRequest = {
      studentId: submission.studentId,
      submissionId: submission.submissionId.toString(),
      attachmentId: file.attachmentId.toString(),
      filename: file.title,
      source: "EMA"
    }

    console.log("Initiate Scan for:", file.attachmentId);
    this.addItemToStorage(file.attachmentId);
    this.copyLeaksService.initiateScan(initiateRequest,this.role).subscribe({
      next: (res:any) => {
        console.log(res, "Submitted rescan");
        this.checkForScan(file);
      },
      error: (err:any) => {
        console.error("THERE WAS AN ERROR INITIATING SCAN", err);
        file.status = this.StatusEnum.error;
        this.removeItemFromStorage(file.attachmentId);
      }
    });
  }

  searchClick() {
    if (this.role == UserTypes.EMA) {
      if (this.studentId.length > 0) {
        this.location.go('dashboard/' + this.studentId);
        this.getStudentSubmissions(this.studentId);
      }
    } else {
      if (this.studentId.length > 0) {
        this.location.go('sam/dashboard/' + this.studentId);
        this.getStudentSubmissions(this.studentId);
      }
    }
  }

  checkForScan(attachment: Attachment) {
    let dashboardStorage = localStorage.getItem(this.similarityDashboardData);
    if(dashboardStorage?.includes(attachment.attachmentId.toString())) {
      attachment.status = this.StatusEnum.processing;
      this.copyLeaksService.getSubmissionAttachment(this.currentStudentId, attachment.attachmentId.toString(),this.role).subscribe({
        next: (res:any) => {
          if(res.results) {
            attachment.status = this.StatusEnum.complete;
            attachment.similarityScore = res.results.score.aggregatedScore;
            this.removeItemFromStorage(attachment.attachmentId);
          }
        },
        error: (err:any) => {}
      })
    }
  }

  initiateStorage() {
    let currentData = localStorage.getItem(this.similarityDashboardData);
    if(!currentData) {
      const initData = {
        processingIds: []
      }
      localStorage.setItem(this.similarityDashboardData, JSON.stringify(initData));
    }
  }

  addItemToStorage(attachmentId: number) {
    let currentData = localStorage.getItem(this.similarityDashboardData);
    if(currentData) {
      let dataObj = JSON.parse(currentData);
      dataObj.processingIds.push(attachmentId);
      localStorage.setItem(this.similarityDashboardData, JSON.stringify(dataObj));
    }
  }

  removeItemFromStorage(attachmentId: number) {
    let currentData = localStorage.getItem(this.similarityDashboardData);
    if(currentData) {
      let dataObj = JSON.parse(currentData);
      let index = dataObj.processingIds.findIndex((id: number) => id === attachmentId);
      dataObj.processingIds.splice(index, 1);
      localStorage.setItem(this.similarityDashboardData, JSON.stringify(dataObj));
    }
  }

  getPagedResults(pageNumber: number) {
    this.length = this.filteredSubmissionList.length;
    if(this.filteredSubmissionList.length < this.pageSize) this.pagedSubmissionList = this.filteredSubmissionList;

    let start = pageNumber * this.pageSize;
    let end = start + this.pageSize;
    this.pageIndex = pageNumber;

    this.pagedSubmissionList = this.filteredSubmissionList.slice(start, end);
  }

  handlePageEvent(e: PageEvent) {
    this.pageEvent = e;
    this.length = e.length;
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;

    this.getPagedResults(this.pageIndex);
  }

  sortData(sort: any) {
    const data = this.filteredSubmissionList.slice();
    if (!sort.active || sort.direction === '') {
      this.filteredSubmissionList = data;
      return;
    }

    this.filteredSubmissionList = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'dateSubmitted':
          return this.compare(a.dateSubmitted, b.dateSubmitted, isAsc);
        case 'taskName':
          return this.compare(a.taskName, b.taskName, isAsc);
        case 'attempt':
          return this.compare(a.attempt, b.attempt, isAsc);
        case 'assessmentCode':
          return this.compare(a.assessmentCode, b.assessmentCode, isAsc);
        case 'files':
          let lengthA = a.attachments ? a.attachments.length : 0;
          let lengthB = b.attachments ? b.attachments.length : 0; 
          return this.compare(lengthA, lengthB, isAsc);
        case 'submissionId':
          return this.compare(a.submissionId, b.submissionId, isAsc);
        case 'taskId':
          return this.compare(a.taskId, b.taskId, isAsc);
        default:
          return 0;
      }
    });

    this.getPagedResults(this.pageIndex);
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
 
}
